import { BulkVisitsActionBar } from '@/components/bulkQueue/BulkVisitsActionBar';
import { ActionsRenderCell } from '@/components/bulkQueue/cells/ActionsRender';
import { FulfillmentWindowRenderCell } from '@/components/bulkQueue/cells/FulfillmentWindowRender';
import { OrderNotesRenderCell, OrderRenderCell } from '@/components/bulkQueue/cells/OrderRender';
import { PatientRenderCell } from '@/components/bulkQueue/cells/PatientRender';
import { PriorityRenderCell } from '@/components/bulkQueue/cells/PriorityRender';
import { ClinicianRenderCell } from '@/components/bulkQueue/cells/clinician/ClinicianRender';
import { DateRenderCell } from '@/components/bulkQueue/cells/date/DateRender';
import {
    DateSelectionCell,
    DateSelectionHeader,
} from '@/components/bulkQueue/cells/date/DateSelection';
import { ServicesRenderCell } from '@/components/bulkQueue/cells/services/ServicesRender';
import { getSetCellProps } from '@/components/bulkQueue/tables/components';
import { VisitTableRow } from '@/components/bulkQueue/tables/queries';
import { BulkTentativeDateAssignmentTableQuery } from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateAssignmentTable.gql.types';
import {
    BulkTentativeDateContextProvider,
    useBulkTentativeDateContext,
} from '@/components/bulkQueue/tables/tentativeDate/BulkTentativeDateContext';
import { useConnectedBulkTable } from '@/components/bulkQueue/tables/useConnectedBulkTable';
import { Pagination } from '@/components/bulkQueue/tables/useUnifiedPagination';
import { BulkTableRow } from '@/components/bulkQueue/types';
import { rowToLink } from '@/components/bulkQueue/utils';
import { MapFriendlyVisit } from '@/components/mapping/VisitsMap/VisitsMap.types';
import { useSetMapFriendlyVisits } from '@/components/mapping/VisitsMap/useSetMapFriendlyVisits';
import { AxleTable, RawColumn, ServerSideSort } from '@/components/tables/AxleTable';
import { visitOrVisitRequestTableRowFragmentToMapFriendlyVisits } from '@/components/visits/utils/visitOrVisitRequestTableRowFragmentToMapFriendlyVisits';
import { PAGE_BODY_SKELETON_HEIGHT, SimpleSkeleton } from '@/fetching/suspense';
import { useHasVisitCancelPermission } from '@/hooks/permissions/visit';
import { FeatureFlag } from '@/types/featureFlag';
import { VisitState } from '@/types/visit';
import { useFeatureFlagEnabled } from '@/utils/useFeatureFlagEnabled';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

export const BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY = gql`
    query BulkTentativeDateAssignmentTable(
        $visitFilters: VisitFilter
        $visitPagination: PaginationInput
        $visitOrdering: VisitOrdering
    ) {
        visits(filters: $visitFilters, pagination: $visitPagination, ordering: $visitOrdering) {
            totalCount
            items {
                ...VisitTableRow
            }
        }
    }
    ${VisitTableRow}
`;

type BulkTentativeDateAssignmentTableProps = {
    rows: BulkTableRow[];
    pagination: Pagination;
    title?: string;
    serverSideSort: ServerSideSort;
    hasBulkCancel: boolean;
    selectedRowValues: string[];
    setSelectedRowValues: (values: string[]) => void;
};

const rowToSelectionId = (row: BulkTableRow) => row.id;

const getColumns = (
    hasEditableItems: boolean,
    rows: BulkTableRow[],
    hasOrders: boolean,
): RawColumn<BulkTableRow>[] => [
    {
        field: 'sortPriority',
        label: 'Priority',
        rowToComponent: (row) => <PriorityRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'patient.fullName',
        label: 'Patient',
        rowToComponent: (row) => <PatientRenderCell entity={row} />,
    },
    {
        field: 'sortServicesName',
        label: 'Services',
        rowToComponent: (row) => <ServicesRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'cliniciansSortKey',
        label: 'Clinician',
        rowToComponent: (row) => <ClinicianRenderCell entity={row} />,
        options: { sort: false },
    },
    {
        field: 'sortDate',
        label: 'Date',
        rowToComponent: (row) =>
            row.__typename === 'Visit' && !row.permissions.reschedule ? (
                <DateRenderCell entity={row} />
            ) : (
                <DateSelectionCell entity={row} />
            ),
        options: {
            setCellProps: (_value: unknown, rowIndex: number) => {
                const row = rows[rowIndex];
                const isEditable = row.__typename !== 'Visit' || row.permissions.reschedule;
                return getSetCellProps(hasEditableItems && isEditable ? true : null);
            },
            customHeadRender: (columnMeta, handleToggleColumn, sortOrder) => (
                <DateSelectionHeader
                    key={columnMeta.name + '-header'}
                    columnMeta={columnMeta}
                    handleToggleColumn={handleToggleColumn}
                    sortOrder={sortOrder}
                    hasEditableItems={hasEditableItems}
                />
            ),
        },
    },
    {
        field: 'fulfillmentWindow.startDate',
        label: 'Fulfillment Window',
        rowToComponent: (row) => <FulfillmentWindowRenderCell entity={row} />,
    },
    ...(hasOrders
        ? [
              {
                  field: 'orderSortKey',
                  label: 'Order',
                  rowToComponent: (row: BulkTableRow) => <OrderRenderCell entity={row} />,
                  options: { sort: false },
              },
              {
                  label: 'Order Notes',
                  rowToComponent: (row: BulkTableRow) => <OrderNotesRenderCell entity={row} />,
              },
          ]
        : []),
    {
        field: '',
        label: 'Actions',
        rowToComponent: (row) => <ActionsRenderCell entity={row} />,
    },
];

export const BulkTentativeDateAssignmentTable: FC<BulkTentativeDateAssignmentTableProps> = (
    props,
) => {
    const {
        rows,
        pagination,
        title,
        serverSideSort,
        hasBulkCancel,
        selectedRowValues,
        setSelectedRowValues,
    } = props;
    const hasEditableItems = rows.some((r) => r.__typename !== 'Visit' || r.permissions.reschedule);
    const hasOrders = useFeatureFlagEnabled(FeatureFlag.ORDERS);
    const columns = useMemo(
        () => getColumns(hasEditableItems, rows, hasOrders),
        [hasEditableItems, rows, hasOrders],
    );
    const { selectedCount } = useBulkTentativeDateContext();
    // Hide row selection (and its select-all header checkbox) when there are no
    // rows to select. AxleTable requires all selection props to agree.
    const showBulkSelect = hasBulkCancel && rows.length > 0;
    return (
        <AxleTable
            title={
                typeof title === 'string' ? (
                    <Typography variant='h2'>{title}</Typography>
                ) : undefined
            }
            dynamicPaging={pagination}
            data={rows}
            toolbar={false}
            rowToLink={rowToLink}
            columns={columns}
            serverSideSort={serverSideSort}
            selectableRows={showBulkSelect ? 'multiple' : 'none'}
            disableSelection={selectedCount > 0}
            rowToSelectionIdentifier={showBulkSelect ? rowToSelectionId : undefined}
            selectedRowValues={showBulkSelect ? selectedRowValues : undefined}
            setSelectedRowValues={showBulkSelect ? setSelectedRowValues : undefined}
        />
    );
};

type ConnectedBulkTentativeDateAssignmentTableProps = {
    title?: string;
};

type CancelVisitsActionBarProps = {
    rows: BulkTableRow[];
    selectedRowValues: string[];
};

const CancelVisitsActionBar: FC<CancelVisitsActionBarProps> = ({ rows, selectedRowValues }) => {
    const selectedSet = useMemo(() => new Set(selectedRowValues), [selectedRowValues]);
    const cancellableVisits = useMemo(
        () =>
            rows
                .filter(
                    (row) =>
                        row.__typename === 'Visit' &&
                        selectedSet.has(row.id) &&
                        row.state !== VisitState.ENDED &&
                        row.permissions.cancel,
                )
                .map((row) => ({
                    visitId: row.id,
                    patientId: row.patient.id,
                    patientName: row.patient.fullName,
                    city: row.address?.city,
                })),
        [rows, selectedSet],
    );

    return (
        <BulkVisitsActionBar
            selectedCount={selectedRowValues.length}
            cancellableVisits={cancellableVisits}
            showCancel
            showReassign={false}
        />
    );
};

export const ConnectedBulkTentativeDateAssignmentTable: FC<
    ConnectedBulkTentativeDateAssignmentTableProps
> = (props) => {
    const { title } = props;
    const { rows, pagination, serverSideSort, showSkeleton } =
        useConnectedBulkTable<BulkTentativeDateAssignmentTableQuery>({
            query: BULK_TENTATIVE_DATE_ASSIGNMENT_TABLE_QUERY,
        });
    const bulkCancelEnabled = useFeatureFlagEnabled(
        FeatureFlag.DASHBOARD_BULK_CANCELLATION_ALL_VISITS,
    );
    const hasBulkCancel = useHasVisitCancelPermission() && bulkCancelEnabled;
    const [selectedRowValues, setSelectedRowValues] = useState<string[]>([]);

    useEffect(() => {
        const rowIds = new Set(rows.map((r) => r.id));
        setSelectedRowValues((prev) => {
            const filtered = prev.filter((id) => rowIds.has(id));
            return filtered.length === prev.length ? prev : filtered;
        });
    }, [rows]);

    const setMapFriendlyVisits: () => MapFriendlyVisit[] = useCallback(() => {
        return visitOrVisitRequestTableRowFragmentToMapFriendlyVisits(rows);
    }, [rows]);

    useSetMapFriendlyVisits(setMapFriendlyVisits);

    if (showSkeleton) {
        return <SimpleSkeleton height={PAGE_BODY_SKELETON_HEIGHT} />;
    }

    return (
        <BulkTentativeDateContextProvider entities={rows} disabled={selectedRowValues.length > 0}>
            {hasBulkCancel && (
                <CancelVisitsActionBar rows={rows} selectedRowValues={selectedRowValues} />
            )}
            <BulkTentativeDateAssignmentTable
                rows={rows}
                title={title}
                pagination={pagination}
                serverSideSort={serverSideSort}
                hasBulkCancel={hasBulkCancel}
                selectedRowValues={selectedRowValues}
                setSelectedRowValues={setSelectedRowValues}
            />
        </BulkTentativeDateContextProvider>
    );
};
